import { createContext, useRef, useState } from "react";
import { useLocation } from "wouter";
import axios, { setAuthToken } from "../api/axiosConfig.js";
import {
  sendErrorToast,
  sendServerErrorToastWithFallbackCode,
  sendServerFriendlyToastWithFallbackCode,
} from "~/utils/handleToasts";
import { AxiosError } from "axios";
import { LOCAL_STORAGE_USER } from "./consts.js";
import { CredentialResponse } from "google-one-tap";
import { sendAnalyticsEvent } from "./analytics.js";
import * as userApi from "../api/userApi.js";
import { UserProfile } from "../api/userApi.js";
import * as Sentry from "@sentry/react";
import { identifyUser, resetUser } from "../api/analyticsApi.js";
declare global {
  const google: typeof import("google-one-tap");
}

export const getFallbackUuid = () => {
  // generate new UUID
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, function (c) {
    const r = (Math.random() * 16) | 0,
      v = c === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
};
// --- API Interfaces ---------------

const SIGN_IN_ENDPOINT = "auth/signin";
const SIGN_UP_ENDPOINT = "auth/signup";
const GOOGLE_SIGN_IN_ENDPOINT = "auth/google";
const APPLE_SIGN_IN_ENDPOINT = "auth/apple";
const FORGOT_PASSWORD_ENDPOINT = "auth/forgotPassword";
const RESET_PASSWORD_ENDPOINT = "auth/resetPassword";
const CHECK_PASSWORD_ENDPOINT = "auth/checkPasswordLink";

interface SignUpRequest {
  email: string;
  name: string;
  password: string;
  profilePic: string;
  referrerId: string | null;
}

interface ForgotPasswordRequest {
  email: string;
  captcha: string;
  origin: string;
}

interface ResetPasswordRequest {
  password: string;
  token: string;
}

interface AuthContext {
  user: UserProfile | null;
  token: string | null;
  getUser: () => Promise<void>;
  updateUser: (update: Partial<UserProfile>) => Promise<void>;
  addOrRemoveFavorite: (uuid: string) => Promise<void>;
  clearUser: () => void;
  signup: (
    email: string,
    name: string,
    password: string,
    profilePic: string,
    referrerId: string | null
  ) => Promise<void>;
  loginEmail: (email: string, password: string) => void;
  loginGoogle: (googleResponse: CredentialResponse, referrerId?: string | null) => void;
  loginApple: (idToken: string, name?: string | null, referrerId?: string | null) => Promise<void>;
  logout: () => void;
  forgotPassword: (email: string, captcha: string) => Promise<void>;
  resetPassword: (password: string, token: string) => Promise<void>;
  checkPasswordLink: (token: string) => Promise<void>;
  processDeckPayment: (
    deckIds: string[],
    paymentIntentId: string,
    paymentGroupId: string
  ) => Promise<void>;
}
export const AuthContext = createContext<AuthContext>({
  user: null,
  token: null,
  getUser: async () => { },
  updateUser: async () => { },
  addOrRemoveFavorite: async () => { },
  clearUser: () => { },
  signup: async () => { },
  loginEmail: () => { },
  loginGoogle: () => { },
  loginApple: async () => { },
  logout: () => { },
  forgotPassword: async () => { },
  resetPassword: async () => { },
  checkPasswordLink: async () => { },
  processDeckPayment: async () => { },
});

export function ProvideAuth({ children }: any) {
  const localStorageEntry = localStorage?.getItem(LOCAL_STORAGE_USER);

  const savedUser = localStorageEntry ? JSON.parse(localStorageEntry) : null;
  const savedToken = localStorageEntry
    ? JSON.parse(localStorageEntry).token
    : null;
  const [user, setUser] = useState<UserProfile | null>(savedUser);
  const [token, setToken] = useState<string | null>(savedToken);
  let [, setLocation] = useLocation();
  // id of the latest updateUser call, so stale responses can't clobber it
  const updateUserRequestId = useRef(0);

  const clearUser = () => {
    localStorage.removeItem(LOCAL_STORAGE_USER);
    setUser(null);
  };

  const handleErrorWithFallbackCode = (
    error: any,
    fallbackErrorCode: number
  ) => {
    sendServerErrorToastWithFallbackCode(error, fallbackErrorCode);
    // Handle AxiosErrors
    if (error && error instanceof AxiosError && error.response) {
      if (error.response.status === 401) {
        // User is not authenticated
        clearUser();
        Sentry.captureMessage("Unauthenticated user error", "log");
        return;
      }
      if (error.response.status === 403) {
        // User is not authorized
        Sentry.captureMessage("Unauthorized action error", "warning");
        return;
      }
      if (error.response.status === 404) {
        // Resource not found
        Sentry.captureMessage("Resource not found error", "warning");
        return;
      }
      if (error.response.status === 500) {
        // Server error
        Sentry.captureException(error);
        return;
      }
    }
    if (error) throw error;
  };

  const getUser = async (): Promise<void> => {
    try {
      const user = await userApi.getCurrentUser();
      // update local storage user with user data
      const localStorageString =
        localStorage?.getItem(LOCAL_STORAGE_USER) || "";
      const localStorageData = JSON.parse(localStorageString);
      if (localStorageData) {
        localStorage.setItem(
          LOCAL_STORAGE_USER,
          JSON.stringify({ ...localStorageData, ...user })
        );
      }
      setUser(user);

      // Identify user in analytics if they're already logged in
      await identifyUser(user.name, user.email, user.username, user.isPlus, user.birthday);
    } catch (error) {
      // Unknown error logging in
      handleErrorWithFallbackCode(error, 1001);
    }
  };

  const updateUser = async (update: Partial<UserProfile>) => {
    // optimistic update, guarded by requestId against out-of-order responses
    const requestId = ++updateUserRequestId.current;
    const previousUser = user;
    if (user) {
      setUser({ ...user, ...update });
    }
    try {
      const updatedUser = await userApi.updateUser(update);
      if (requestId !== updateUserRequestId.current) return;

      // update local storage user with updatedUser data
      const localStorageString =
        localStorage?.getItem(LOCAL_STORAGE_USER) || "";
      const localStorageData = JSON.parse(localStorageString);

      localStorage.setItem(
        LOCAL_STORAGE_USER,
        JSON.stringify({ ...localStorageData, ...updatedUser })
      );
      setUser(updatedUser);

      // Re-identify user in analytics if profile information changed
      if (update.name || update.email) {
        await identifyUser(updatedUser.name, updatedUser.email, updatedUser.username, updatedUser.isPlus, updatedUser.birthday);
      }
    } catch (error) {
      if (requestId === updateUserRequestId.current) {
        // roll back, unless a newer update already superseded this one
        setUser(previousUser);
      }
      // User update error
      handleErrorWithFallbackCode(error, 1003);
    }
  };

  const processDeckPayment = async (
    deckIds: string[],
    paymentIntentId: string,
    paymentGroupId: string
  ): Promise<void> => {
    try {
      const updatedUser = await userApi.processDeckPayment(
        deckIds,
        paymentIntentId,
        paymentGroupId
      );
      // update local storage user with updatedUser data
      const localStorageString =
        localStorage?.getItem(LOCAL_STORAGE_USER) || "";
      const localStorageData = JSON.parse(localStorageString);

      localStorage.setItem(
        LOCAL_STORAGE_USER,
        JSON.stringify({ ...localStorageData, ...updatedUser })
      );
      setUser(updatedUser);
    } catch (error) {
      handleErrorWithFallbackCode(error, 1022);
    }
  };

  const addOrRemoveFavorite = async (favoriteUuid: string) => {
    if (!user) {
      return;
    }
    const currentFavorites = user.favorites;
    const favoriteIndex = currentFavorites.indexOf(favoriteUuid);
    let newFavorites = [...currentFavorites];
    if (favoriteIndex >= 0) {
      newFavorites.splice(favoriteIndex, 1);
    } else {
      newFavorites.push(favoriteUuid);
    }
    updateUser({ favorites: newFavorites });
    sendAnalyticsEvent("edited_favorites");
  };

  const signup = async (
    email: string,
    name: string,
    password: string,
    profilePic: string,
    referrerId: string | null
  ) => {
    try {
      const signUpData: SignUpRequest = {
        email: email,
        name: name,
        password: password,
        profilePic: profilePic,
        referrerId: referrerId ? referrerId : null,
      };

      await axios.post(SIGN_UP_ENDPOINT, signUpData);
      sendAnalyticsEvent("sign_up");
      return await loginEmail(email, password);
    } catch (error) {
      handleErrorWithFallbackCode(error, 1004);
      throw error;
    }
  };

  const loginEmail = async (email: string, password: string): Promise<void> => {
    try {
      const authData = (await axios.post(SIGN_IN_ENDPOINT, { email, password }))
        .data as UserProfile & { token: string };
      if (!authData.token) {
        // No token returned from login
        sendErrorToast(1028);
      }
      sendAnalyticsEvent("sign_in");

      setToken(authData.token);
      setAuthToken(authData.token);

      localStorage.setItem(LOCAL_STORAGE_USER, JSON.stringify(authData));

      // remove token, and set user to memory
      let userData: any = authData;
      delete userData.token;
      const user = userData as UserProfile;
      setUser(user);

      // Identify user in analytics
      await identifyUser(user.name, user.email, user.username, user.isPlus, user.birthday);
    } catch (error) {
      // Login email failed
      handleErrorWithFallbackCode(error, 1002);
    }
  };

  const loginGoogle = async (
    googleResponse: CredentialResponse,
    referrerId?: string | null
  ): Promise<void> => {
    try {
      console.log('🌱 Frontend - sending referrerId to backend:', referrerId)
      const authData = (
        await axios.post(GOOGLE_SIGN_IN_ENDPOINT, {
          credential: googleResponse.credential,
          referrerId: referrerId
        })
      ).data as UserProfile & { token: string; isNewUser: boolean };
      await completeSocialLogin(authData, "google_sign_in");
    } catch (error) {
      handleErrorWithFallbackCode(error, 1012);
    }
  };

  const loginApple = async (
    idToken: string,
    name?: string | null,
    referrerId?: string | null
  ): Promise<void> => {
    try {
      const authData = (
        await axios.post(APPLE_SIGN_IN_ENDPOINT, {
          idToken,
          name,
          referrerId,
        })
      ).data as UserProfile & { token: string; isNewUser: boolean };
      await completeSocialLogin(authData, "apple_sign_in");
    } catch (error) {
      handleErrorWithFallbackCode(error, 1186);
      throw error;
    }
  };

  const completeSocialLogin = async (
    authData: UserProfile & { token: string; isNewUser: boolean },
    analyticsEvent: string
  ) => {
    if (authData.isNewUser) {
      sendAnalyticsEvent("sign_up");
    }
    sendAnalyticsEvent(analyticsEvent);

    setToken(authData.token);
    setAuthToken(authData.token);

    // remove isNewUser, and save to local storage
    let localStorageData: any = authData;
    delete localStorageData.isNewUser;
    localStorage.setItem(
      LOCAL_STORAGE_USER,
      JSON.stringify(localStorageData)
    );

    // remove token, and set user to memory
    let userData: any = localStorageData;
    delete userData.token;

    const user = userData as UserProfile;
    setUser(user);

    // Identify user in analytics
    await identifyUser(user.name, user.email, user.username, user.isPlus, user.birthday);
  };

  const logout = () => {
    // Reset user in analytics before clearing user data
    if (user) {
      resetUser(user.username);
    }
    clearUser();
    sendAnalyticsEvent("sign_out");
    setLocation("/");
  };

  const forgotPassword = async (
    email: string,
    captcha: string
  ): Promise<void> => {
    try {
      const forgotPasswordData: ForgotPasswordRequest = {
        email: email,
        captcha: captcha,
        origin: location.origin,
      };
      const res = await axios.post<any, any & { message: string }>(
        FORGOT_PASSWORD_ENDPOINT,
        forgotPasswordData
      );
      sendAnalyticsEvent("forgot_password_issued");
      // Link sent successfully
      sendServerFriendlyToastWithFallbackCode(res, 1006);
    } catch (error) {
      // Error with password reset
      handleErrorWithFallbackCode(error, 1005);
    }
  };

  const resetPassword = async (
    password: string,
    token: string
  ): Promise<void> => {
    try {
      const resetPasswordData: ResetPasswordRequest = {
        password: password,
        token: token,
      };
      await axios.post(RESET_PASSWORD_ENDPOINT, resetPasswordData);
      sendAnalyticsEvent("password_reset");
    } catch (error) {
      handleErrorWithFallbackCode(error, 1007);
    }
  };

  const checkPasswordLink = async (token: string): Promise<void> => {
    try {
      await axios.post(CHECK_PASSWORD_ENDPOINT, {
        token: token,
      });
    } catch (error) {
      handleErrorWithFallbackCode(error, 1008);
    }
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        token,
        getUser,
        updateUser,
        addOrRemoveFavorite,
        clearUser,
        signup,
        loginEmail,
        loginGoogle,
        loginApple,
        logout,
        forgotPassword,
        resetPassword,
        checkPasswordLink,
        processDeckPayment,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}
